import React, { useState, useRef } from 'react';
import {
  ListItem,
  Avatar,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ZoomIn as ZoomInIcon,
  Download as DownloadIcon,
  Reply as ReplyIcon
} from '@mui/icons-material';
import { TranslateMessage } from './TranslateMessage';
import type { ChatMessage } from '../pages/Community';

export interface MessageBubbleProps {
  message: ChatMessage;
  currentUserId?: string;
  isMobile: boolean;
  highlightedMessageId: string | null;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  
  // Helper functions
  formatTimestamp: (timestamp: any) => string;
  renderMessageContent: (content: string) => React.ReactNode;
  canShowMessageMenu: (message: ChatMessage) => boolean;
  
  // Event handlers
  onMessageMenuOpen: (event: React.MouseEvent<HTMLElement>, message: ChatMessage) => void;
  onImageViewerOpen: (imageUrl: string) => void;
  onImageDownload: (imageUrl: string, imageName?: string) => void;
  onQuickReaction: (emoji: string, message: ChatMessage) => void;
  onQuickReactionOpen: (event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>, message: ChatMessage) => void;
  onLongPressStart: (event: React.TouchEvent<HTMLElement>, message: ChatMessage) => void;
  onLongPressEnd: () => void;
  onTouchEnd: (event: React.TouchEvent) => void;
  onReplyClick: (messageId: string) => void;
  onReplyToMessage?: (message: ChatMessage) => void; // New prop for swipe-to-reply
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  currentUserId,
  isMobile,
  highlightedMessageId,
  messagesContainerRef,
  formatTimestamp,
  renderMessageContent,
  canShowMessageMenu,
  onMessageMenuOpen,
  onImageViewerOpen,
  onImageDownload,
  onQuickReaction,
  onQuickReactionOpen,
  onLongPressStart,
  onLongPressEnd,
  onTouchEnd,
  onReplyClick,
  onReplyToMessage
}) => {
  const isOwnMessage = message.userId === currentUserId;
  
  // Swipe-to-reply state
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const lastSwipeX = useRef(0);
  const swipeThreshold = 80; // Minimum swipe distance to trigger reply
  const maxSwipeOffset = 100; // Maximum visual offset during swipe
  
  // Swipe gesture handlers for mobile
  const handleSwipeStart = (e: React.TouchEvent) => {
    if (!isMobile || !onReplyToMessage) return;
    
    const touch = e.touches[0];
    swipeStartX.current = touch.clientX;
    swipeStartY.current = touch.clientY;
    lastSwipeX.current = touch.clientX;
    setIsSwipeActive(true);
  };

  const handleSwipeMove = (e: React.TouchEvent) => {
    if (!isMobile || !isSwipeActive || !onReplyToMessage) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStartX.current;
    const deltaY = Math.abs(touch.clientY - swipeStartY.current);
    
    // Only allow horizontal swipes (ignore if too much vertical movement)
    if (deltaY > 30) {
      handleSwipeEnd();
      return;
    }
    
    // Only allow right swipes (positive deltaX)
    if (deltaX > 0) {
      const clampedOffset = Math.min(deltaX, maxSwipeOffset);
      setSwipeOffset(clampedOffset);
      lastSwipeX.current = touch.clientX;
    }
  };

  const handleSwipeEnd = () => {
    if (!isMobile || !isSwipeActive || !onReplyToMessage) return;
    
    const finalDelta = lastSwipeX.current - swipeStartX.current;
    
    // Trigger reply if swipe threshold is met
    if (finalDelta >= swipeThreshold) {
      onReplyToMessage(message);
    }
    
    // Reset swipe state
    setIsSwipeActive(false);
    setSwipeOffset(0);
    swipeStartX.current = 0;
    swipeStartY.current = 0;
    lastSwipeX.current = 0;
  };
  
  return (
    <ListItem 
      key={message.id}
      data-message-id={message.id}
      onTouchStart={isMobile ? handleSwipeStart : undefined}
      onTouchMove={isMobile ? handleSwipeMove : undefined}
      onTouchEnd={isMobile ? handleSwipeEnd : undefined}
      sx={{ 
        py: isMobile ? 0.5 : 1, // Tighter vertical spacing on mobile
        alignItems: 'flex-start',
        flexDirection: isOwnMessage ? 'row-reverse' : 'row',
        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
        // Reduced horizontal padding on mobile for more message space
        pl: isOwnMessage ? (isMobile ? 4 : 8) : (isMobile ? 0.5 : 1),
        pr: isOwnMessage ? (isMobile ? 0.5 : 1) : (isMobile ? 4 : 4),
        backgroundColor: highlightedMessageId === message.id ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
        transition: 'background-color 0.3s ease, transform 0.1s ease',
        // Apply swipe transform on mobile
        transform: isMobile && swipeOffset > 0 ? `translateX(${swipeOffset}px)` : 'none',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {/* Swipe-to-reply visual indicator (mobile only) */}
      {isMobile && swipeOffset > 20 && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: Math.min(swipeOffset / swipeThreshold, 1),
            transition: 'opacity 0.1s ease',
            zIndex: 0
          }}
        >
          <ReplyIcon 
            sx={{ 
              color: 'white',
              fontSize: 20,
              transform: swipeOffset >= swipeThreshold ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.1s ease'
            }}
          />
        </Box>
      )}
      
      <Avatar sx={{ 
        mr: isOwnMessage ? 0 : (isMobile ? 1 : 1.5), 
        ml: isOwnMessage ? (isMobile ? 1 : 1.5) : 0, 
        mt: 0.5,
        bgcolor: isOwnMessage ? 'primary.main' : 'secondary.main',
        color: isOwnMessage ? 'primary.contrastText' : 'secondary.contrastText',
        // Smaller avatar on mobile for more message space
        width: isMobile ? 24 : 28,
        height: isMobile ? 24 : 28,
        fontSize: isMobile ? '10px' : '12px',
        fontWeight: 500
      }}>
        {message.userDisplayName?.[0]?.toUpperCase() || 'A'}
      </Avatar>
      <Box sx={{ 
        flex: 1, 
        // Increased max width on mobile for better space usage
        maxWidth: isMobile 
          ? (isOwnMessage ? '88%' : '85%') // Much wider on mobile
          : (isOwnMessage ? '75%' : '70%'), // Original desktop size
        textAlign: 'left',
        ml: isOwnMessage ? 'auto' : 0,
        mr: isOwnMessage ? 0 : 'auto',
        transform: 'none'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: isMobile ? 0.2 : 0.3, // Tighter spacing on mobile
          flexDirection: isOwnMessage ? 'row-reverse' : 'row',
          gap: isMobile ? 0.25 : 0.5 // Closer elements on mobile
        }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 500,
            fontSize: isMobile ? '10px' : '11px', // Slightly smaller on mobile
            color: 'text.disabled'
          }}>
            {isOwnMessage ? 'You' : (message.userDisplayName || 'Anonymous')}
          </Typography>
          <Typography variant="caption" sx={{ 
            color: 'text.disabled',
            fontSize: isMobile ? '9px' : '10px' // Slightly smaller on mobile
          }}>
            {formatTimestamp(message.timestamp)}
            {message.isEdited && (
              <span style={{ marginLeft: '4px', opacity: 0.7 }}>
                (edited)
              </span>
            )}
          </Typography>
        </Box>
        <Box 
          sx={{
            backgroundColor: '#424242',
            color: '#f5f5f5',
            // Compact padding on mobile for more content space
            padding: message.type === 'image' 
              ? '4px' 
              : isMobile 
                ? '8px 12px'  // Reduced from 10px 14px on mobile
                : '10px 14px', // Original desktop padding
            borderRadius: isMobile ? '12px' : '8px', // More modern rounded corners on mobile
            borderTopRightRadius: isOwnMessage ? '2px' : (isMobile ? '12px' : '8px'),
            borderTopLeftRadius: isOwnMessage ? (isMobile ? '12px' : '8px') : '2px',
            display: isOwnMessage ? 'block' : 'inline-block',
            width: isOwnMessage ? 'fit-content' : 'auto',
            minWidth: isOwnMessage ? (isMobile ? '40px' : '60px') : 'auto', // Smaller minimum on mobile
            maxWidth: '100%',
            wordWrap: 'break-word',
            border: 'none',
            position: 'relative',
            cursor: 'pointer',
            ml: isOwnMessage ? 'auto' : 0,
            '&:hover .message-options': {
              opacity: 1
            },
            '&:hover .quick-reactions': {
              opacity: 1
            }
          }}
          onTouchStart={(e) => {
            // Only trigger long press if not swiping
            if (!isSwipeActive) {
              onLongPressStart(e, message);
            }
          }}
          onTouchEnd={(e) => {
            // Only trigger touch end if not swiping
            if (!isSwipeActive) {
              onTouchEnd(e);
            }
          }}
          onTouchMove={(e) => {
            // Only trigger long press end if not swiping
            if (!isSwipeActive) {
              onLongPressEnd();
            }
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            onQuickReactionOpen(e, message);
          }}
        >
          {canShowMessageMenu(message) && (
            <Box
              className="message-options"
              sx={{
                position: 'absolute',
                top: 4,
                right: isOwnMessage ? 4 : 'auto',
                left: isOwnMessage ? 'auto' : 4,
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                zIndex: 1
              }}
            >
              <Tooltip title="Message options">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMessageMenuOpen(e, message);
                  }}
                  sx={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    width: 24,
                    height: 24,
                    '&:hover': { 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)' 
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          
          {/* Quick Reaction Buttons (WhatsApp-style) */}
          <Box
            className="quick-reactions"
            sx={{
              position: 'absolute',
              bottom: -18,
              right: isOwnMessage ? 'auto' : -4,
              left: isOwnMessage ? -4 : 'auto',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              zIndex: 2,
              display: 'flex',
              gap: 0.5,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: '18px',
              padding: '4px 8px',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            {['👍', '❤️', '😂'].map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickReaction(emoji, message);
                }}
                sx={{
                  fontSize: '14px',
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.2)'
                  }
                }}
              >
                {emoji}
              </IconButton>
            ))}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuickReactionOpen(e, message);
              }}
              sx={{
                fontSize: '12px',
                minWidth: '28px',
                height: '28px',
                borderRadius: '50%',
                color: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  transform: 'scale(1.1)'
                }
              }}
            >
              +
            </IconButton>
          </Box>
          
          {message.type === 'voice' && message.audioUrl ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? 0.5 : 1, 
              minWidth: isMobile ? '250px' : '200px', // Wider on mobile
              maxWidth: '100%'
            }}>
              <audio 
                controls 
                controlsList="nodownload"
                src={message.audioUrl}
                style={{ 
                  width: '100%',
                  height: isMobile ? '36px' : '40px', // Slightly smaller on mobile
                  borderRadius: '4px'
                }}
                preload="metadata"
              />
              {message.audioDuration && (
                <Typography variant="caption" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: isMobile ? '9px' : '10px',
                  whiteSpace: 'nowrap'
                }}>
                  {Math.floor(message.audioDuration / 60)}:{String(message.audioDuration % 60).padStart(2, '0')}
                </Typography>
              )}
            </Box>
          ) : message.type === 'image' && message.imageUrl ? (
            <Box>
              <Box sx={{ 
                position: 'relative', 
                display: 'inline-block',
                '&:hover .image-overlay': {
                  opacity: 1
                }
              }}>
                <img 
                  src={message.imageUrl} 
                  alt={message.imageName || 'Shared image'}
                  style={{
                    // Much larger images on mobile for better visibility
                    maxWidth: isMobile ? '280px' : '200px',
                    maxHeight: isMobile ? '280px' : '200px',
                    borderRadius: isMobile ? '8px' : '6px',
                    display: 'block',
                    cursor: 'pointer',
                    width: '100%', // Make images fill available space
                    height: 'auto'
                  }}
                  onClick={() => onImageViewerOpen(message.imageUrl!)}
                  onLoad={(e) => {
                    // Auto-scroll when image loads
                    if (messagesContainerRef.current) {
                      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                    }
                  }}
                />
                <Box
                  className="image-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                    gap: 1
                  }}
                >
                  <Tooltip title="View Full Size">
                    <IconButton
                      size="small"
                      sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onImageViewerOpen(message.imageUrl!);
                      }}
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Image">
                    <IconButton
                      size="small"
                      sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)'
                        }  
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onImageDownload(message.imageUrl!, message.imageName);
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ 
                color: '#f5f5f5',
                fontSize: isMobile ? '15px' : '14px', // Slightly larger on mobile for readability
                lineHeight: isMobile ? 1.4 : 1.5, // Tighter line height on mobile for more content
                wordBreak: 'break-word' // Better text wrapping on mobile
              }}>
                {renderMessageContent(message.content)}
              </Typography>
              
              {/* Translation Component */}
              <TranslateMessage
                messageId={message.id}
                originalText={message.content}
                compact={isMobile}
              />
            </>
          )}
          
          {/* Reply indicator */}
          {message.replyTo && (
            <Tooltip title="Click to go to original message" placement="top">
              <Box 
                onClick={() => onReplyClick(message.replyTo!.messageId)}
                sx={{
                  mb: 1,
                  p: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Typography variant="caption" sx={{ 
                  color: 'primary.main', 
                  fontWeight: 500,
                  display: 'block'
                }}>
                  ↳ {message.replyTo.userDisplayName}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}>
                  {message.replyTo.type === 'image' ? '📷 Image' : message.replyTo.content}
                </Typography>
              </Box>
            </Tooltip>
          )}
          
          {/* Reactions */}
          {message.reactions && Object.keys(message.reactions).length > 0 && (
            <Box sx={{ 
              mt: 0.5, 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5,
              maxWidth: '100%'
            }}>
              {Object.entries(message.reactions).map(([emoji, reaction]) => (
                <Chip
                  key={emoji}
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ fontSize: '12px' }}>{emoji}</span>
                      <span style={{ fontSize: '11px' }}>{reaction.count}</span>
                    </Box>
                  }
                  onClick={() => onQuickReaction(emoji, message)}
                  sx={{
                    height: '24px',
                    backgroundColor: reaction.users.includes(currentUserId || '') 
                      ? 'rgba(25, 118, 210, 0.2)' 
                      : 'rgba(255, 255, 255, 0.1)',
                    border: reaction.users.includes(currentUserId || '') 
                      ? '1px solid rgba(25, 118, 210, 0.5)' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: reaction.users.includes(currentUserId || '') 
                        ? 'rgba(25, 118, 210, 0.3)' 
                        : 'rgba(255, 255, 255, 0.15)',
                      transform: 'scale(1.05)'
                    },
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ListItem>
  );
};

export default MessageBubble; 